import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  LinearProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/msalConfig';
import {
  SportsBasketball,
  SportsVolleyball,
  Login,
  LocationOn,
  CalendarToday,
  People,
  Email,
  Facebook,
  Twitter,
  Instagram,
  Menu as MenuIcon,
  Close as CloseIcon,
  AttachMoney,
  QrCode,
  Payment,
  CloudUpload,
  CheckCircle,
  ArrowBack
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { v4 as uuidv4 } from 'uuid';

// Add CSS keyframes for animations
const styles = `
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showNotification } = useApp();
  const { instance } = useMsal();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Simple state for tournaments
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [, setCurrentImageIndex] = useState(0);
  const [registrationStep, setRegistrationStep] = useState('form'); // 'form', 'payment', 'confirmation'
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [registrationData, setRegistrationData] = useState({
    firstName: '',
    lastName: '',
    teamName: '',
    email: '',
    phone: '',
    gender: '',
    ageUnder18: false,
    skillLevel: '',
    parentGuardianName: '',
    consent: false
  });

  // Dynamic background images - automatically load all images from home_page folder
  const backgroundImages = (() => {
    try {
      // Import all images from the home_page folder
      const imageContext = require.context('../images/home_page', false, /\.(jpg|jpeg|png|gif|webp)$/i);
      return imageContext.keys().map(imageContext);
    } catch (error) {
      console.warn('Could not load images from home_page folder:', error);
      // Fallback to empty array if folder doesn't exist
      return [];
    }
  })();

  // Auto-rotate background images (only if images are available)
  useEffect(() => {
    if (backgroundImages.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % backgroundImages.length
      );
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  // Get sport color based on sport type (currently unused but available for future use)
  // const getSportColor = (sport, opacity = 1) => {
  //   const colors = {
  //     'Tennis': `rgba(34, 197, 94, ${opacity})`,
  //     'Basketball': `rgba(59, 130, 246, ${opacity})`,
  //     'Volleyball': `rgba(168, 85, 247, ${opacity})`,
  //     'Badminton': `rgba(236, 72, 153, ${opacity})`,
  //     'Squash': `rgba(245, 158, 11, ${opacity})`,
  //     'Soccer': `rgba(16, 185, 129, ${opacity})`
  //   };
  //   return colors[sport] || `rgba(107, 114, 128, ${opacity})`;
  // };

  // Simple API call on page load
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/public/api/v1/tournaments');
        console.log('API Response:', response.data);
        // Extract tournaments from the nested data property
        setTournaments(response.data.data || []);
      } catch (error) {
        console.error('Error fetching tournaments:', error);
        setTournaments([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };
    
    fetchTournaments();
  }, []);

  // Handle URL-based registration popup
  useEffect(() => {
    const registerParam = searchParams.get('register');
    if (registerParam && tournaments.length > 0) {
      const tournament = tournaments.find(t => t.id === registerParam);
      if (tournament) {
        handleRegistrationOpen(tournament);
        // Don't clear URL parameter immediately - keep it visible
      }
    }
  }, [tournaments, searchParams, setSearchParams]);


  const sports = [
    { name: 'Badminton', icon: <SportsBasketball />, color: '#2196F3' },
    { name: 'Pickleball', icon: <SportsVolleyball />, color: '#FF9800' },
    { name: 'Basketball', icon: <SportsBasketball />, color: '#FF5722' }
  ];

  const skillLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

  const handleRegistrationOpen = (tournament) => {
    setSelectedTournament(tournament);
    setRegistrationDialogOpen(true);
    setRegistrationData({
      firstName: '',
      lastName: '',
      teamName: '',
      email: '',
      phone: '',
      gender: '',
      ageUnder18: '',
      skillLevel: '',
      consent: false
    });
  };

  const handleRegisterClick = (tournament) => {
    // Update URL with registration parameter
    setSearchParams({ register: tournament.id });
    // Open registration dialog
    handleRegistrationOpen(tournament);
  };

  const handleRegistrationClose = () => {
    resetRegistration();
    // Clear URL parameter when dialog is closed
    setSearchParams({});
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation function to check if all required fields are filled and consent is given
  const isFormValid = () => {
    const basicValidation = registrationData.firstName && 
           registrationData.lastName && 
           registrationData.teamName && 
           registrationData.email && 
           isValidEmail(registrationData.email) &&
           registrationData.phone && 
           registrationData.gender && 
           registrationData.skillLevel && 
           registrationData.consent;
    
    // If under 18 is true, parent/guardian name is required
    if (registrationData.ageUnder18) {
      return basicValidation && registrationData.parentGuardianName;
    }
    
    return basicValidation;
  };

  const scrollToTop = () => {
    // Find the dialog content element and scroll to top
    const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root');
    if (dialogContent) {
      dialogContent.scrollTop = 0;
    }
  };

  const handleRegistrationSubmit = async () => {
    // Validate required fields
    if (!isFormValid()) {
      if (registrationData.email && !isValidEmail(registrationData.email)) {
        showNotification('Please enter a valid email address.', 'warning');
      } else {
        showNotification('Please fill in all required fields and agree to the consent.', 'warning');
      }
      return;
    }
    
    // Move to payment step and scroll to top
    setRegistrationStep('payment');
    // Use setTimeout to ensure the DOM has updated before scrolling
    setTimeout(scrollToTop, 100);
  };

  const handlePaymentComplete = async () => {
    if (!paymentScreenshot) {
      showNotification('Please upload a screenshot of your payment confirmation.', 'error');
      return;
    }

    try {
      setLoading(true);
      const now = Date.now();
      
      // Convert screenshot to base64
      const base64Screenshot = await convertToBase64(paymentScreenshot);
      
      const playerData = {
        id: uuidv4(),
        ...registrationData,
        tournamentId: selectedTournament.id,
        tournamentName: selectedTournament.name,
        registrationDate: now,
        createdAt: now,
        updatedAt: now,
        registrationFeePaid: true,
        registrationFeeScreenshot: base64Screenshot
      };

      // Ensure ageUnder18 is a boolean
      playerData.ageUnder18 = Boolean(playerData.ageUnder18);
      
      console.log('Sending player data with payment:', playerData);
      
      const response = await axios.post('/public/api/v1/players', playerData);
      console.log('Registration successful:', response.data);
      
      setRegistrationStep('confirmation');
      setTimeout(scrollToTop, 100);
    } catch (error) {
      console.error('Registration failed:', error);
      
      // Handle different types of API errors
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 400) {
          // Handle validation errors
          if (data.invalidParams && data.invalidParams.length > 0) {
            // Display the first invalid parameter message
            const firstError = data.invalidParams[0];
            showNotification(firstError.message || 'Invalid input provided.', 'error');
          } else if (data.detail) {
            // Display the detail message if no invalidParams
            showNotification(data.detail, 'error');
          } else {
            showNotification('Invalid data provided. Please check your inputs.', 'error');
          }
        } else if (status === 409) {
          // Handle conflict errors (e.g., email already exists)
          showNotification('Email already registered for this tournament.', 'error');
        } else if (status === 404) {
          // Handle not found errors
          showNotification('Tournament not found or no longer available.', 'error');
        } else if (status >= 500) {
          // Handle server errors
          showNotification('Server error. Please try again later.', 'error');
        } else {
          // Handle other client errors
          if (data.detail) {
            showNotification(data.detail, 'error');
          } else if (data.title) {
            showNotification(data.title, 'error');
          } else {
            showNotification(`Registration failed with status ${status}. Please try again.`, 'error');
          }
        }
      } else if (error.request) {
        // Handle network errors
        showNotification('Network error. Please check your connection and try again.', 'error');
      } else {
        // Handle other errors
        showNotification('Registration failed. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPaymentScreenshot(file);
    }
  };

  const resetRegistration = () => {
    setRegistrationStep('form');
    setPaymentScreenshot(null);
    setRegistrationDialogOpen(false);
    setSelectedTournament(null);
    setRegistrationData({
      firstName: '',
      lastName: '',
      teamName: '',
      email: '',
      phone: '',
      gender: '',
      ageUnder18: false,
      skillLevel: '',
      parentGuardianName: '',
      consent: false
    });
  };

  // Handle admin login - redirect directly to Azure
  const handleAdminLogin = async () => {
    try {
      console.log('Starting admin login redirect...');
      console.log('Login request:', loginRequest);
      
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Admin login failed:', error);
      showNotification('Admin login failed. Please try again.', 'error');
    }
  };

  const getProgressPercentage = (tournament) => {
    // Use currentRegistrations from API response
    return Math.round((tournament.currentRegistrations / tournament.maxParticipants) * 100);
  };

  const isRegistrationOpen = (tournament) => {
    const deadline = tournament.registrationDeadline; // Already in milliseconds
    const now = Date.now();
    return now < deadline && tournament.status === 'active';
  };

  const getDaysUntilDeadline = (tournament) => {
    const deadline = tournament.registrationDeadline; // Already in milliseconds
    const now = Date.now();
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const renderPaymentStep = () => {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            💳 Complete Your Registration
          </Typography>
          <Typography variant="body2">
            Please pay the registration fee via Zelle to finish the registration and secure your spot in the tournament.
          </Typography>
        </Alert>

        {/* Tournament Details */}
        <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a365d' }}>
            Tournament Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              <strong>Tournament:</strong> {selectedTournament?.name}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              <strong>Registration Fee:</strong> {selectedTournament?.currencyCode === 'USD' ? '$' : ''}{selectedTournament?.registrationFee} {selectedTournament?.currencyCode || 'USD'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              <strong>Payment Method:</strong> {selectedTournament?.registrationFeePaymentSystem || 'Zelle'}
            </Typography>
          </Box>
        </Paper>

        {/* Payment Instructions */}
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            '& .MuiAlert-icon': {
              color: '#856404'
            }
          }}
        >
          <Typography variant="body2" sx={{ color: '#856404', fontWeight: 500 }}>
            <strong>Include your full name in the Zelle message</strong>
          </Typography>
        </Alert>

        {/* Payment Options */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
          {/* QR Code Section */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a365d' }}>
              Scan with your banking app
            </Typography>
            {selectedTournament?.registrationFeePaymentUrl && (
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'white', 
                borderRadius: 2, 
                border: '1px solid #e0e0e0',
                display: 'inline-block',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(selectedTournament.registrationFeePaymentUrl)}`}
                  alt="Zelle QR Code"
                  style={{ width: '200px', height: '200px' }}
                />
              </Box>
            )}
          </Box>

          {/* OR Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 2 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
            <Typography variant="body2" sx={{ mx: 2, color: '#666', fontWeight: 500 }}>
              OR
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
          </Box>

          {/* Mobile Payment Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a365d' }}>
              Pay with Zelle
            </Typography>
            {selectedTournament?.registrationFeePaymentUrl && (
              <Button
                variant="contained"
                size="large"
                startIcon={<Payment />}
                onClick={() => window.open(selectedTournament.registrationFeePaymentUrl, '_blank')}
                sx={{
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Open Zelle Payment
              </Button>
            )}
          </Box>
        </Box>

        {/* Screenshot Upload */}
        <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a365d' }}>
            Upload Payment Confirmation
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Please upload a screenshot of your payment confirmation to complete your registration.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="payment-screenshot-upload"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="payment-screenshot-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)'
                  }
                }}
              >
                {paymentScreenshot ? 'Change Screenshot' : 'Upload Screenshot'}
              </Button>
            </label>
            
            {paymentScreenshot && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 500 }}>
                  ✓ {paymentScreenshot.name} uploaded successfully
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderConfirmationStep = () => {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', textAlign: 'center' }}>
        <Box sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: '#4caf50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          animation: 'bounceIn 0.6s ease',
          '@keyframes bounceIn': {
            '0%': { transform: 'scale(0)' },
            '50%': { transform: 'scale(1.2)' },
            '100%': { transform: 'scale(1)' }
          }
        }}>
          <CheckCircle sx={{ fontSize: 40, color: 'white' }} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a365d', mb: 2 }}>
          Registration Complete! 🎉
        </Typography>

        <Typography variant="h6" sx={{ color: '#4a5568', mb: 3, maxWidth: '500px' }}>
          Congratulations! You have successfully registered for {selectedTournament?.name} and your payment has been confirmed.
        </Typography>

        <Paper sx={{ p: 4, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 3, maxWidth: '400px', width: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1a365d' }}>
            Registration Summary
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Name:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {registrationData.firstName} {registrationData.lastName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Email:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{registrationData.email}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Tournament:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedTournament?.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Registration Fee:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedTournament?.currencyCode === 'USD' ? '$' : ''}{selectedTournament?.registrationFee} {selectedTournament?.currencyCode || 'USD'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Status:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#4caf50' }}>
                ✓ Paid & Confirmed
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Typography variant="body2" sx={{ color: '#666', maxWidth: '500px' }}>
          You will receive a confirmation email shortly with tournament details and next steps. 
          Thank you for choosing VeiledKnights!
        </Typography>
      </Box>
    );
  };

  const renderRegistrationForm = () => {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
        {/* Scroll indicator */}
        {true && (
          <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid #e0e0e0',
            py: 1.5,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ 
                color: '#1976d2', 
                fontWeight: 500,
                fontSize: '0.875rem'
              }}>
                Scroll down to see all fields
              </Typography>
              <Box sx={{
                animation: 'bounce 2s infinite',
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': {
                    transform: 'translateY(0)'
                  },
                  '40%': {
                    transform: 'translateY(-4px)'
                  },
                  '60%': {
                    transform: 'translateY(-2px)'
                  }
                }
              }}>
                ↓
              </Box>
            </Box>
            <Typography variant="caption" sx={{ 
              color: '#666', 
              fontSize: '0.75rem',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              Don't forget to read and accept the terms at the bottom
            </Typography>
          </Box>
        )}
        <TextField
          fullWidth
          label="First Name"
          value={registrationData.firstName}
          onChange={(e) => setRegistrationData({...registrationData, firstName: e.target.value})}
          required
          variant="outlined"
        />
        
        <TextField
          fullWidth
          label="Last Name"
          value={registrationData.lastName}
          onChange={(e) => setRegistrationData({...registrationData, lastName: e.target.value})}
          required
          variant="outlined"
        />
        
        <TextField
          fullWidth
          label="Team Name"
          value={registrationData.teamName}
          onChange={(e) => setRegistrationData({...registrationData, teamName: e.target.value})}
          required
          variant="outlined"
        />
        
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={registrationData.email}
          onChange={(e) => setRegistrationData({...registrationData, email: e.target.value})}
          required
          variant="outlined"
          error={registrationData.email && !isValidEmail(registrationData.email)}
          helperText={registrationData.email && !isValidEmail(registrationData.email) ? 'Please enter a valid email address' : ''}
        />
        
        <TextField
          fullWidth
          label="Phone Number"
          value={registrationData.phone}
          onChange={(e) => setRegistrationData({...registrationData, phone: e.target.value})}
          required
          variant="outlined"
        />
        
        <FormControl fullWidth required variant="outlined">
          <InputLabel>Gender</InputLabel>
          <Select
            value={registrationData.gender}
            onChange={(e) => setRegistrationData({...registrationData, gender: e.target.value})}
            label="Gender"
          >
            <MenuItem value="M">M</MenuItem>
            <MenuItem value="F">F</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl fullWidth required variant="outlined">
          <InputLabel>Under 18?</InputLabel>
          <Select
            value={registrationData.ageUnder18 ? 'true' : 'false'}
            onChange={(e) => setRegistrationData({...registrationData, ageUnder18: e.target.value === 'true'})}
            label="Under 18?"
          >
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>
        
        {registrationData.ageUnder18 && (
          <TextField
            fullWidth
            required
            variant="outlined"
            label="Parent/Guardian Name"
            value={registrationData.parentGuardianName}
            onChange={(e) => setRegistrationData({...registrationData, parentGuardianName: e.target.value})}
            sx={{ mb: 2 }}
          />
        )}
        
        <FormControl fullWidth required variant="outlined">
          <InputLabel>Skill Level</InputLabel>
          <Select
            value={registrationData.skillLevel}
            onChange={(e) => setRegistrationData({...registrationData, skillLevel: e.target.value})}
            label="Skill Level"
          >
            {skillLevels.map((level) => (
              <MenuItem key={level} value={level}>{level}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Box>
          <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
              VeiledKnights Tournament Waiver, Release of Liability & Data Consent
          </Typography>
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 1,
              p: 2,
              maxHeight: '150px',
              overflowY: 'auto',
              backgroundColor: '#f9f9f9'
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
              {selectedTournament?.waiver || 'Loading waiver information...'}
            </Typography>
          </Box>
        </Box>
        
        <FormControlLabel
          control={
            <Checkbox
              checked={registrationData.consent}
              onChange={(e) => setRegistrationData({...registrationData, consent: e.target.checked})}
              required
              color="primary"
            />
          }
          label={selectedTournament?.acknowledgment || "I agree to the terms and conditions above"}
          sx={{ alignItems: 'flex-start' }}
        />
        
        {/* Floating completion indicator */}
        {true && (() => {
          const totalFields = 8 + (registrationData.ageUnder18 ? 1 : 0);
          const completedFields = [
            registrationData.firstName,
            registrationData.lastName,
            registrationData.teamName,
            registrationData.email && isValidEmail(registrationData.email),
            registrationData.phone,
            registrationData.gender,
            registrationData.skillLevel,
            registrationData.consent,
            ...(registrationData.ageUnder18 ? [registrationData.parentGuardianName] : [])
          ].filter(Boolean).length;
          const isComplete = completedFields === totalFields;
          
          return (
            <Box sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {/* Floating completion dots */}
              <Box sx={{
                display: 'flex',
                gap: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                px: 2,
                py: 1,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(25, 118, 210, 0.2)',
                transform: isComplete ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease'
              }}>
                {Array.from({ length: totalFields }, (_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: index < completedFields ? '#1976d2' : '#e0e0e0',
                      transition: 'all 0.3s ease',
                      animation: index < completedFields ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.6 },
                        '100%': { opacity: 1 }
                      }
                    }}
                  />
                ))}
              </Box>
              
              {/* Completion checkmark */}
              {isComplete && (
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  animation: 'bounceIn 0.6s ease',
                  '@keyframes bounceIn': {
                    '0%': { transform: 'scale(0)' },
                    '50%': { transform: 'scale(1.2)' },
                    '100%': { transform: 'scale(1)' }
                  }
                }}>
                  ✓
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>
    );
  };

  return (
    <Box>
      {/* Navigation */}
      <AppBar position="sticky" elevation={0} sx={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        color: 'text.primary', 
        zIndex: 1100 
      }}>
        <Toolbar sx={{ px: { xs: 2, sm: 3, md: 4 }, position: 'relative', minHeight: '80px' }}>
          {/* Logo on the left */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img 
              src={require('../images/new_logo.png')} 
              alt="VeiledKnights Logo"
              style={{
                height: '60px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'contrast(1.1) brightness(1.05)',
                imageRendering: 'high-quality'
              }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                color: '#1a365d',
                fontSize: { xs: '1.1rem', sm: '1.3rem' },
                letterSpacing: '-0.5px'
              }}
            >
              VeiledKnights
            </Typography>
          </Box>

          {/* Navigation items absolutely centered */}
          {isMobile ? (
            <IconButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)} sx={{ ml: 'auto' }}>
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          ) : (
            <Box sx={{ 
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex', 
              gap: { xs: 1, sm: 2, md: 3 }, 
              alignItems: 'center'
            }}>
              <Button 
                color="inherit" 
                href="#tournaments"
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  fontWeight: 500,
                  color: '#4a5568',
                  textTransform: 'none',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: '#1976d2'
                  }
                }}
              >
                Tournaments
              </Button>
              <Button 
                color="inherit" 
                href="#sports"
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  fontWeight: 500,
                  color: '#4a5568',
                  textTransform: 'none',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: '#1976d2'
                  }
                }}
              >
                Sports
              </Button>
              <Button 
                color="inherit" 
                href="#contact"
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  fontWeight: 500,
                  color: '#4a5568',
                  textTransform: 'none',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: '#1976d2'
                  }
                }}
              >
                Contact
              </Button>
            </Box>
          )}

          {/* Admin Login on the right */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              <Button
                variant="contained"
                startIcon={<Login />}
                onClick={handleAdminLogin}
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  fontWeight: 600,
                  px: { xs: 3, sm: 4, md: 5 },
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Admin Login
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: 'white',
            color: 'text.primary'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#151c29' }}>
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <List>
            <ListItem 
              button 
              onClick={() => {
                setMobileMenuOpen(false);
                document.getElementById('tournaments')?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{ 
                borderRadius: 1, 
                mb: 1,
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
            >
              <ListItemText 
                primary="Tournaments" 
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    color: '#151c29'
                  } 
                }} 
              />
            </ListItem>
            
            <ListItem 
              button 
              onClick={() => {
                setMobileMenuOpen(false);
                document.getElementById('sports')?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{ 
                borderRadius: 1, 
                mb: 1,
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
            >
              <ListItemText 
                primary="Sports" 
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    color: '#151c29'
                  } 
                }} 
              />
            </ListItem>
            
            <ListItem 
              button 
              onClick={() => {
                setMobileMenuOpen(false);
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{ 
                borderRadius: 1, 
                mb: 1,
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
            >
              <ListItemText 
                primary="Contact" 
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    color: '#151c29'
                  } 
                }} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Button
            variant="contained"
            startIcon={<Login />}
            onClick={handleAdminLogin}
            fullWidth
            sx={{ 
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            Admin Login
          </Button>
        </Box>
      </Drawer>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          minHeight: { xs: '50vh', sm: '45vh', md: '50vh' }
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 2, sm: 3, md: 4 },
            minHeight: { xs: 'auto', sm: '35vh', md: '40vh' },
            flexDirection: { xs: 'column', sm: 'row' },
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                position: 'relative',
                flexShrink: 0,
                mb: { xs: 2, sm: 0 }
              }}>
                {/* Background decorative elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '100px', sm: '150px', md: '180px' },
                    height: { xs: '100px', sm: '150px', md: '180px' },
                    background: 'radial-gradient(circle, rgba(25, 118, 210, 0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    zIndex: 0
                  }}
                />
                
                {/* Logo with enhanced styling */}
                <Box
                  sx={{
                    width: { xs: '80px', sm: '120px', md: '140px' },
                    height: { xs: '80px', sm: '120px', md: '140px' },
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: '-2px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
                      zIndex: -1,
                      animation: 'rotate 20s linear infinite'
                    }
                  }}
                >
                  <img
                    src={require('../images/new_logo.png')}
                    alt="VeiledKnights Logo"
                    style={{
                      width: '80%',
                      height: '80%',
                      objectFit: 'contain',
                      filter: 'contrast(1.1) brightness(1.05)',
                      borderRadius: '50%'
                    }}
                  />
                </Box>
              </Box>
            </motion.div>

            {/* Text and Buttons */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ flex: 1, width: '100%' }}
            >
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem' },
                    fontWeight: 800,
                    color: '#1a365d',
                    lineHeight: 1.2,
                    mb: { xs: 1, sm: 1.5 },
                    letterSpacing: '-0.02em'
                  }}
                >
                  Empowering Community Through
                  <Box component="span" sx={{ 
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'block',
                    fontSize: { xs: '1.3rem', sm: '1.6rem', md: '2rem' }
                  }}>
                    Sports Excellence
                  </Box>
                </Typography>
                
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                    color: '#4a5568',
                    lineHeight: 1.4,
                    mb: { xs: 2, sm: 3 },
                    fontWeight: 400,
                    maxWidth: { xs: '100%', sm: '90%', md: '80%' }
                  }}
                >
                  Join VeiledKnights and be part of a community that celebrates strength, 
                  determination, and the power of sports to bring people together.
                </Typography>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1, sm: 1.5 }, 
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', sm: 'flex-start' }
              }}>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={() => document.getElementById('tournaments').scrollIntoView({ behavior: 'smooth' })}
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    fontWeight: 600,
                    px: { xs: 2.5, sm: 3, md: 4 },
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                    minWidth: { xs: '120px', sm: '140px' },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Join Tournament
                </Button>
                
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => document.getElementById('sports').scrollIntoView({ behavior: 'smooth' })}
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    fontWeight: 600,
                    px: { xs: 2.5, sm: 3, md: 4 },
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    textTransform: 'none',
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    borderWidth: 2,
                    minWidth: { xs: '120px', sm: '140px' },
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Upcoming Tournaments Section */}
      <Box id="tournaments" sx={{ 
        py: { xs: 6, sm: 8, md: 10 }, 
        backgroundColor: '#ffffff',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(to right, transparent, #e2e8f0, transparent)'
        }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: '#1a365d',
                mb: 2,
                letterSpacing: '-0.02em'
              }}
            >
              Upcoming Tournaments
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                color: '#4a5568',
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Join our competitive tournaments and showcase your skills in a supportive environment
            </Typography>
          </Box>
          
          {/* Tournament Cards Container */}
          <Box sx={{ 
            backgroundColor: '#f8fafc',
            borderRadius: 4,
            p: { xs: 3, sm: 4, md: 6 },
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
          }}>
            <Grid container spacing={3} sx={{ alignItems: 'stretch', justifyContent: 'center' }}>
              {loading ? (
                <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>Loading tournaments...</Typography>
                </Grid>
              ) : tournaments && tournaments.length > 0 ? (
                tournaments.map((tournament, index) => (
                <Grid item xs={12} sm={6} md={6} key={tournament.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    style={{ width: '100%', maxWidth: '350px' }}
                  >
                    <Card 
                      sx={{ 
                        height: { xs: '750px', sm: '650px' }, // Increased height for both mobile and desktop for registration button visibility
                        width: '100%',
                        minWidth: '300px',
                        maxWidth: '350px',
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 3,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                          borderColor: '#1976d2'
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', p: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                        <Typography variant="h6" component="h3" sx={{ 
                          fontWeight: 700, 
                          fontSize: '1.2rem', 
                          lineHeight: 1.3,
                          color: '#1a365d'
                        }}>
                          {tournament.name}
                        </Typography>
                          <Chip 
                            label={tournament.sport} 
                            sx={{ 
                              fontWeight: 600,
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              border: '1px solid #bbdefb'
                            }}
                            size="small"
                          />
                        </Box>
                        
                        <Typography variant="body2" sx={{ 
                          mb: 3, 
                          fontSize: '0.95rem', 
                          lineHeight: 1.5, 
                          minHeight: '50px',
                          color: '#4a5568'
                        }}>
                          {tournament.description}
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CalendarToday sx={{ mr: 1.5, fontSize: 18, color: '#1976d2' }} />
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              color: '#1a365d'
                            }}>
                              {new Date(tournament.startDate).toLocaleDateString()} - {new Date(tournament.endDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <LocationOn sx={{ mr: 1.5, fontSize: 18, color: '#1976d2' }} />
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              color: '#1a365d'
                            }}>{tournament.location}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <People sx={{ mr: 1.5, fontSize: 18, color: '#1976d2' }} />
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              color: '#1a365d'
                            }}>
                              {tournament.currentRegistrations}/{tournament.maxParticipants} participants
                            </Typography>
                          </Box>
                          {tournament.registrationFee && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                              <AttachMoney sx={{ mr: 1.5, fontSize: 18, color: '#1976d2' }} />
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                color: '#1a365d'
                              }}>
                                Registration Fee: {tournament.currencyCode === 'USD' ? '$' : ''}{tournament.registrationFee} {tournament.currencyCode || 'USD'}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 600,
                              color: '#1a365d',
                              fontSize: '0.9rem'
                            }}>
                              Registration Progress
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 700,
                              color: '#1976d2',
                              fontSize: '0.9rem'
                            }}>
                              {getProgressPercentage(tournament)}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={getProgressPercentage(tournament)} 
                            sx={{ 
                              height: 10, 
                              borderRadius: 5,
                              backgroundColor: '#e3f2fd',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#1976d2',
                                borderRadius: 5
                              }
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="body2" sx={{ 
                            fontSize: '0.9rem',
                            color: '#4a5568',
                            fontWeight: 500
                          }}>
                            {isRegistrationOpen(tournament) 
                              ? `${getDaysUntilDeadline(tournament)} days left to register`
                              : 'Registration closed'
                            }
                          </Typography>
                          <Chip 
                            label={isRegistrationOpen(tournament) ? 'Open' : 'Closed'} 
                            sx={{
                              backgroundColor: isRegistrationOpen(tournament) ? '#e8f5e8' : '#f5f5f5',
                              color: isRegistrationOpen(tournament) ? '#2e7d32' : '#757575',
                              fontWeight: 600,
                              border: isRegistrationOpen(tournament) ? '1px solid #c8e6c9' : '1px solid #e0e0e0'
                            }}
                            size="small"
                          />
                        </Box>
                        
                        {/* Spacer to push button to bottom */}
                        <Box sx={{ flexGrow: 1, minHeight: { xs: '10px', sm: '40px' } }} />
                      </CardContent>
                      
                      <CardActions sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 1, sm: 2 }, pb: { xs: 3, sm: 4 }, mt: 'auto' }}>
                        <Button 
                          fullWidth 
                          variant="contained"
                          disabled={!isRegistrationOpen(tournament)}
                          onClick={() => handleRegisterClick(tournament)}
                          sx={{ 
                            height: '52px',
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            textTransform: 'none',
                            borderRadius: 3,
                            background: isRegistrationOpen(tournament) 
                              ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                              : '#e0e0e0',
                            color: isRegistrationOpen(tournament) ? 'white' : '#757575',
                            boxShadow: isRegistrationOpen(tournament) 
                              ? '0 4px 12px rgba(25, 118, 210, 0.3)'
                              : 'none',
                            '&:hover': isRegistrationOpen(tournament) ? {
                              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                              transform: 'translateY(-1px)'
                            } : {},
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {isRegistrationOpen(tournament) ? 'Register Now' : 'Registration Closed'}
                        </Button>
                      </CardActions>
                    </Card>
                  </motion.div>
                </Grid>
                ))
              ) : (
                <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>No tournaments available at the moment.</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Custom Divider with Logo After Tournaments */}
      <Box sx={{ 
        py: { xs: 2, sm: 3, md: 4 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        width: '100vw', // Full viewport width
        marginLeft: 'calc(-50vw + 50%)', // Break out of container
        marginRight: 'calc(-50vw + 50%)' // Break out of container
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%',
          px: { xs: 1, sm: 2, md: 3 } // Reduced padding
        }}>
          {/* Left Line */}
          <Box sx={{ 
            flex: 1, 
            height: '2px', 
            background: 'linear-gradient(to right, transparent, #666666)', // Darker line
            mr: 2
          }} />
          
          {/* Logo in Middle */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: { xs: '100px', sm: '120px', md: '140px' }, // Larger logo
            height: { xs: '70px', sm: '80px', md: '90px' }, // Larger logo
            mx: 2
          }}>
            <img 
              src={require('../images/new_logo.png')} 
              alt="VeiledKnights Logo"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                filter: 'contrast(1.1) brightness(1.05)', // Clearer logo
                imageRendering: 'high-quality' // Clearer logo
              }}
            />
          </Box>
          
          {/* Right Line */}
          <Box sx={{ 
            flex: 1, 
            height: '2px', 
            background: 'linear-gradient(to left, transparent, #666666)', // Darker line
            ml: 2
          }} />
        </Box>
      </Box>

      {/* Sports Showcase Section */}
      <Box id="sports" sx={{ 
        py: { xs: 6, sm: 8, md: 10 }, 
        backgroundColor: '#f8fafc',
        position: 'relative'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: '#1a365d',
                mb: 2,
                letterSpacing: '-0.02em'
              }}
            >
              Available Sports
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                color: '#4a5568',
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Discover the sports we offer and find your perfect match
            </Typography>
          </Box>
          
          {/* Sports Cards Container */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderRadius: 4,
            p: { xs: 3, sm: 4, md: 6 },
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
          }}>
            <Grid container spacing={3} sx={{ alignItems: 'stretch', justifyContent: 'center' }}>
              {sports.map((sport, index) => (
                <Grid item xs={12} sm={6} md={6} key={sport.name} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    style={{ width: '100%', maxWidth: '350px' }}
                  >
                    <Card 
                      sx={{ 
                        height: '520px', 
                        width: '100%',
                        minWidth: '300px',
                        maxWidth: '350px',
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 3,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                          borderColor: '#1976d2'
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', p: 4, textAlign: 'center' }}>
                        {/* Sport Icon */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          mb: 4,
                          mt: 2
                        }}>
                          <Box sx={{ 
                            color: '#1976d2', 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 140,
                            height: 140,
                            backgroundColor: '#e3f2fd',
                            borderRadius: '50%',
                            border: '3px solid #bbdefb',
                            boxShadow: '0 8px 24px rgba(25, 118, 210, 0.15)'
                          }}>
                            {React.cloneElement(sport.icon, { sx: { fontSize: 70 } })}
                          </Box>
                        </Box>
                        
                        {/* Sport Name */}
                        <Typography variant="h5" component="h3" sx={{ 
                          fontWeight: 700, 
                          fontSize: '1.3rem', 
                          lineHeight: 1.3,
                          mb: 3,
                          color: '#1a365d'
                        }}>
                          {sport.name}
                        </Typography>
                        
                        {/* Sport Description */}
                        <Typography variant="body1" sx={{ 
                          fontSize: '1rem', 
                          lineHeight: 1.6, 
                          minHeight: '60px',
                          mb: 4,
                          color: '#4a5568'
                        }}>
                          {sport.description || `Join our ${sport.name.toLowerCase()} tournaments and compete with players of all skill levels.`}
                        </Typography>
                        
                        {/* Spacer to push content to center */}
                        <Box sx={{ flexGrow: 1 }} />
                        
                        {/* Sport Features */}
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center' }}>
                            <Box sx={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: '50%', 
                              backgroundColor: '#1976d2', 
                              mr: 1.5 
                            }} />
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.9rem', 
                              color: '#4a5568',
                              fontWeight: 500
                            }}>
                              All skill levels welcome
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center' }}>
                            <Box sx={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: '50%', 
                              backgroundColor: '#1976d2', 
                              mr: 1.5 
                            }} />
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.9rem', 
                              color: '#4a5568',
                              fontWeight: 500
                            }}>
                              Professional equipment provided
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: '50%', 
                              backgroundColor: '#1976d2', 
                              mr: 1.5 
                            }} />
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.9rem', 
                              color: '#4a5568',
                              fontWeight: 500
                            }}>
                              Expert coaching available
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>


      {/* Custom Divider with Logo Before Contact */}
      <Box sx={{ 
        py: { xs: 2, sm: 3, md: 4 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        width: '100vw', // Full viewport width
        marginLeft: 'calc(-50vw + 50%)', // Break out of container
        marginRight: 'calc(-50vw + 50%)' // Break out of container
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%',
          px: { xs: 1, sm: 2, md: 3 } // Reduced padding
        }}>
          {/* Left Line */}
          <Box sx={{ 
            flex: 1, 
            height: '2px', 
            background: 'linear-gradient(to right, transparent, #666666)', // Darker line
            mr: 2
          }} />
          
          {/* Logo in Middle */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: { xs: '100px', sm: '120px', md: '140px' }, // Larger logo
            height: { xs: '70px', sm: '80px', md: '90px' }, // Larger logo
            mx: 2
          }}>
            <img 
              src={require('../images/new_logo.png')} 
              alt="VeiledKnights Logo"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                filter: 'contrast(1.1) brightness(1.05)', // Clearer logo
                imageRendering: 'high-quality' // Clearer logo
              }}
            />
          </Box>
          
          {/* Right Line */}
          <Box sx={{ 
            flex: 1, 
            height: '2px', 
            background: 'linear-gradient(to left, transparent, #666666)', // Darker line
            ml: 2
          }} />
        </Box>
      </Box>

      {/* Contact Section */}
      <Box id="contact" sx={{ 
        py: { xs: 6, sm: 8, md: 10 }, 
        backgroundColor: '#ffffff',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(to right, transparent, #e2e8f0, transparent)'
        }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: '#1a365d',
                mb: 2,
                letterSpacing: '-0.02em'
              }}
            >
              Get in Touch
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                color: '#4a5568',
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Ready to join our community? We'd love to hear from you
            </Typography>
          </Box>
          
          {/* Contact Cards Container */}
          <Box sx={{ 
            backgroundColor: '#f8fafc',
            borderRadius: 4,
            p: { xs: 3, sm: 4, md: 6 },
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
          }}>
            <Grid container spacing={3} sx={{ alignItems: 'stretch', justifyContent: 'center' }}>
              {/* Contact Info Card */}
              <Grid item xs={12} sm={6} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  style={{ width: '100%', maxWidth: '350px' }}
                >
                  <Card 
                    sx={{ 
                      height: '520px', 
                      width: '100%',
                      minWidth: '300px',
                      maxWidth: '350px',
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 3,
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                        borderColor: '#1976d2'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', p: 4, textAlign: 'center' }}>
                      {/* Contact Icon */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        mb: 4,
                        mt: 2
                      }}>
                        <Box sx={{ 
                          color: '#1976d2', 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 140,
                          height: 140,
                          backgroundColor: '#e3f2fd',
                          borderRadius: '50%',
                          border: '3px solid #bbdefb',
                          boxShadow: '0 8px 24px rgba(25, 118, 210, 0.15)'
                        }}>
                          <Email sx={{ fontSize: 70 }} />
                        </Box>
                      </Box>
                      
                      {/* Contact Title */}
                      <Typography variant="h5" component="h3" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.3rem', 
                        lineHeight: 1.3,
                        mb: 3,
                        color: '#1a365d'
                      }}>
                        Get in Touch
                      </Typography>
                      
                      {/* Contact Details - Only Email */}
                      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          p: 3,
                          backgroundColor: '#f8fafc',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0'
                        }}>
                          <Email sx={{ mr: 2, color: '#1976d2', fontSize: 28 }} />
                          <Typography variant="h6" sx={{ 
                            fontSize: '1.2rem', 
                            fontWeight: 600,
                            color: '#1a365d'
                          }}>
                            info@veiledknights.com
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Contact Description */}
                      <Typography variant="body1" sx={{ 
                        fontSize: '1rem', 
                        lineHeight: 1.6,
                        color: '#4a5568',
                        mb: 4
                      }}>
                        Send us an email and we'll get back to you within 24 hours
                      </Typography>
                      
                      {/* Spacer to push content to center */}
                      <Box sx={{ flexGrow: 1 }} />
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              {/* Social Media Card */}
              <Grid item xs={12} sm={6} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  style={{ width: '100%', maxWidth: '350px' }}
                >
                  <Card 
                    sx={{ 
                      height: '520px', 
                      width: '100%',
                      minWidth: '300px',
                      maxWidth: '350px',
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 3,
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                        borderColor: '#1976d2'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', p: 4, textAlign: 'center' }}>
                      {/* Social Icon */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        mb: 4,
                        mt: 2
                      }}>
                        <Box sx={{ 
                          color: '#1976d2', 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 140,
                          height: 140,
                          backgroundColor: '#e3f2fd',
                          borderRadius: '50%',
                          border: '3px solid #bbdefb',
                          boxShadow: '0 8px 24px rgba(25, 118, 210, 0.15)'
                        }}>
                          <Facebook sx={{ fontSize: 70 }} />
                        </Box>
                      </Box>
                      
                      {/* Social Title */}
                      <Typography variant="h5" component="h3" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.3rem', 
                        lineHeight: 1.3,
                        mb: 3,
                        color: '#1a365d'
                      }}>
                        Follow Us
                      </Typography>
                      
                      {/* Social Links */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 3,
                        mb: 4
                      }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <IconButton sx={{ 
                            color: '#1976d2', 
                            backgroundColor: '#e3f2fd',
                            border: '2px solid #bbdefb',
                            width: 56,
                            height: 56,
                            '&:hover': {
                              backgroundColor: '#bbdefb',
                              transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                          }}>
                            <Facebook sx={{ fontSize: 28 }} />
                          </IconButton>
                          <IconButton sx={{ 
                            color: '#1976d2', 
                            backgroundColor: '#e3f2fd',
                            border: '2px solid #bbdefb',
                            width: 56,
                            height: 56,
                            '&:hover': {
                              backgroundColor: '#bbdefb',
                              transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                          }}>
                            <Twitter sx={{ fontSize: 28 }} />
                          </IconButton>
                          <IconButton sx={{ 
                            color: '#1976d2', 
                            backgroundColor: '#e3f2fd',
                            border: '2px solid #bbdefb',
                            width: 56,
                            height: 56,
                            '&:hover': {
                              backgroundColor: '#bbdefb',
                              transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                          }}>
                            <Instagram sx={{ fontSize: 28 }} />
                          </IconButton>
                        </Box>
                        <Typography variant="body1" sx={{ 
                          fontSize: '1rem', 
                          color: '#4a5568',
                          lineHeight: 1.6,
                          fontWeight: 500
                        }}>
                          Stay updated with our latest tournaments and news
                        </Typography>
                      </Box>
                      
                      {/* Spacer to push content to center */}
                      <Box sx={{ flexGrow: 1 }} />
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Registration Dialog */}
      <Dialog
        open={registrationDialogOpen}
        onClose={handleRegistrationClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          {/* Logo Divider */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 3,
            mt: 1
          }}>
            {/* Left Line */}
            <Box sx={{ 
              flex: 1, 
              height: '2px', 
              background: 'linear-gradient(to right, transparent, #e2e8f0)', 
              mr: 2
            }} />
            
            {/* Logo in Middle */}
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '60px',
              height: '60px',
              mx: 2
            }}>
              <img 
                src={require('../images/new_logo.png')} 
                alt="VeiledKnights Logo"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  filter: 'contrast(1.1) brightness(1.05)',
                  imageRendering: 'high-quality'
                }}
              />
            </Box>
            
            {/* Right Line */}
            <Box sx={{ 
              flex: 1, 
              height: '2px', 
              background: 'linear-gradient(to left, transparent, #e2e8f0)', 
              ml: 2
            }} />
          </Box>
          
          {/* Title */}
          <Box component="div" sx={{ 
            fontWeight: 700, 
            color: '#1a365d',
            fontSize: '1.5rem',
            lineHeight: 1.2
          }}>
            Register for {selectedTournament?.name}
          </Box>
        </DialogTitle>
        
        <DialogContent
          sx={{
            overflow: 'auto',
            maxHeight: 'calc(90vh - 120px)',
            padding: 3,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            }
          }}
        >
          {/* Stepper */}
          <Stepper activeStep={registrationStep === 'form' ? 0 : registrationStep === 'payment' ? 1 : 2} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Registration Form</StepLabel>
            </Step>
            <Step>
              <StepLabel>Payment</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirmation</StepLabel>
            </Step>
          </Stepper>

          {/* Step Content */}
          {registrationStep === 'form' && renderRegistrationForm()}
          {registrationStep === 'payment' && renderPaymentStep()}
          {registrationStep === 'confirmation' && renderConfirmationStep()}
        </DialogContent>
        
        <DialogActions sx={{ padding: 3, paddingBottom: 4, borderTop: '1px solid #e0e0e0' }}>
          {registrationStep === 'form' && (
            <>
              <Button 
                onClick={handleRegistrationClose}
                sx={{
                  fontSize: '1rem',
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  color: '#4a5568',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleRegistrationSubmit}
                disabled={!isFormValid()}
                sx={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  background: isFormValid() 
                    ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                    : 'rgba(0, 0, 0, 0.12)',
                  color: isFormValid() ? 'white' : 'rgba(0, 0, 0, 0.26)',
                  boxShadow: isFormValid() 
                    ? '0 4px 12px rgba(25, 118, 210, 0.3)'
                    : 'none',
                  '&:hover': isFormValid() ? {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)'
                  } : {},
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                    boxShadow: 'none'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Continue to Payment
              </Button>
            </>
          )}
          
          {registrationStep === 'payment' && (
            <>
              <Button 
                onClick={() => {
                  setRegistrationStep('form');
                  setTimeout(scrollToTop, 100);
                }}
                startIcon={<ArrowBack />}
                sx={{
                  fontSize: '1rem',
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  color: '#4a5568',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                Back
              </Button>
              <Button 
                variant="contained" 
                onClick={handlePaymentComplete}
                disabled={!paymentScreenshot || loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Payment />}
                sx={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  background: paymentScreenshot && !loading
                    ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                    : 'rgba(0, 0, 0, 0.12)',
                  color: paymentScreenshot && !loading ? 'white' : 'rgba(0, 0, 0, 0.26)',
                  boxShadow: paymentScreenshot && !loading
                    ? '0 4px 12px rgba(25, 118, 210, 0.3)'
                    : 'none',
                  '&:hover': paymentScreenshot && !loading ? {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)'
                  } : {},
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                    boxShadow: 'none'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? 'Processing...' : 'Complete Registration'}
              </Button>
            </>
          )}
          
          {registrationStep === 'confirmation' && (
            <Button 
              variant="contained" 
              onClick={resetRegistration}
              startIcon={<CheckCircle />}
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                  boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomePage;
